import {Component, computed, DestroyRef, effect, inject, signal} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {ProjectBaseStepComponent} from '../../../project-base-step/project-base-step.component';
import {
  DemonstrationEvaluationControllerService,
  EvaluationDataResponse,
  EvaluationUpsertRequest,
  ProjectRoleType,
  ProjectChapterType,
  ProjectStepType,
  ProjectUserControllerService,
  ProjectUserDetailResponse,
  ProjectSettingControllerService,
  ProjectSettingResponse,
} from '@frontend/shared-api';
import {Router} from '@angular/router';
import {StepDotMenuComponent} from '../../../../../../shared/components/step-dot-menu/step-dot-menu.component';
import {EvaluationContributorFilterDropdownComponent} from './components/evaluation-contributor-filter-dropdown/evaluation-contributor-filter-dropdown.component';
import {ProjectUserDetailService} from '../../../../../../shared/services/project-user-detail.service';
import {AVERAGE_USER_EMAIL} from '../../../../../../shared/constants/contributor.constants';
import {Button} from 'primeng/button';
import {ProviderEvaluationCardComponent} from './components/provider-evaluation-card/provider-evaluation-card.component';
import {EvaluationLegendComponent} from './components/evaluation-legend/evaluation-legend.component';
import {EvaluationSummaryComponent} from './components/evaluation-summary/evaluation-summary.component';
import {CommentModalComponent} from '../../../../../../shared/components/dialog/comment-modal/comment-modal.component';
import {
  DemonstrationAgenda,
  DirectusService,
  EvaluationSubCriteriaDirectus,
  HrTool,
} from '@frontend/directus';
import {EnrichedEvaluationDataResponse, EvaluationAgendaItemVm} from './evaluation-models';
import {firstValueFrom} from 'rxjs';
import {ConfirmationService} from 'primeng/api';
import {SPECIAL_OPTION_IDS} from '../../../project-step-state.constants';
import {APP_ROUTES} from '../../../../../../app.routes';
import {PreviousStepIncompleteNoticeComponent} from '../../../../../../shared/components/previous-step-incomplete-notice/previous-step-incomplete-notice.component';

@Component({
  selector: 'app-step-9-5-provider-evaluation',
  imports: [
    TranslocoPipe,
    StepDotMenuComponent,
    EvaluationContributorFilterDropdownComponent,
    Button,
    ProviderEvaluationCardComponent,
    EvaluationLegendComponent,
    EvaluationSummaryComponent,
    CommentModalComponent,
    PreviousStepIncompleteNoticeComponent,
  ],
  templateUrl: './step-7-5-provider-evaluation.component.html',
})
export class Step75ProviderEvaluationComponent extends ProjectBaseStepComponent {
  private static readonly MIN_REQUIRED_TOOLS_TO_COMPLETE_PHASE = 2;
  private static readonly MINIMUM_PARTICIPANTS_ERROR_DETAIL_KEY =
    'error.rfdProviderEvaluation.minimumParticipantsRequired.detail';

  private readonly evaluationService = inject(DemonstrationEvaluationControllerService);
  private readonly projectUserDetailService = inject(ProjectUserDetailService);
  private readonly projectUserController = inject(ProjectUserControllerService);
  private readonly projectSettingService = inject(ProjectSettingControllerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly directusService = inject(DirectusService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);

  protected readonly contributorRole = ProjectRoleType.CONTRIBUTOR;
  protected readonly accessibleStep = ProjectStepType.RFD_PROVIDER_EVALUATION;
  protected readonly ProjectRoleType = ProjectRoleType;

  private readonly projectIdReady = signal(false);

  protected readonly projectUserDetail = this.projectUserDetailService.createUserDetailSignal(
    () => this.projectId,
    this.destroyRef,
  );

  protected readonly selectedContributorEmail = signal<string | null>(null);
  protected readonly contributors = signal<ProjectUserDetailResponse[]>([]);

  protected readonly evaluationData = signal<EnrichedEvaluationDataResponse | null>(null);
  protected readonly isExponentialScale = signal(false);
  protected readonly hrToolsMap = signal<Map<string, HrTool>>(new Map());
  protected readonly directusAgenda = signal<DemonstrationAgenda[]>([]);

  protected commentModalVisible = false;
  protected currentCommentAgendaItemId: number | null = null;
  protected currentCommentToolId: number | null = null;
  protected currentComments: any[] = [];

  protected readonly isStepPrerequisiteCompleted = computed(() => {
    return this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFD, stepId: ProjectStepType.RFD_INVITATION_LIST},
    ]);
  });

  constructor() {
    super();

    effect(() => {
      if (this.projectIdReady() && this.isStepPrerequisiteCompleted() && !this.evaluationData()) {
        void this.loadData();
      }
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.projectIdReady.set(true);
  }

  private async loadData() {
    if (!this.projectId) return;

    try {
      const [tools, agenda, evaluationDataResponse, contributors, allSubcriteria, projectSettings] =
        (await Promise.all([
          firstValueFrom(this.directusService.loadHrTools()),
          firstValueFrom(this.directusService.loadDemonstrationAgenda()),
          firstValueFrom(this.evaluationService.getEvaluationData({projectId: this.projectId})),
          firstValueFrom(
            this.projectUserController.getProjectUsersWithDetails({projectId: this.projectId}),
          ),
          firstValueFrom(this.directusService.loadSubCriteria()),
          firstValueFrom(
            this.projectSettingService.getProjectSetting({projectId: this.projectId}),
          ).catch(() => null),
        ])) as [
          HrTool[],
          DemonstrationAgenda[],
          EvaluationDataResponse,
          ProjectUserDetailResponse[],
          EvaluationSubCriteriaDirectus[],
          ProjectSettingResponse | null,
        ];

      this.contributors.set(contributors);
      this.directusAgenda.set(agenda);
      this.isExponentialScale.set(Boolean(projectSettings?.isExponential));

      this.hrToolsMap.set(new Map(tools.map((t: HrTool) => [t.id, t])));

      // Merge backend agenda items with Directus translations (topic) and force required ids
      const agendaByDirectusId = new Map(agenda.map((a: DemonstrationAgenda) => [a.id, a]));

      const agendaItemsVm: EvaluationAgendaItemVm[] = (evaluationDataResponse.agendaItems ?? [])
        .filter(
          (i) =>
            i.id != null &&
            i.demonstrationAgendaDirectusId != null &&
            i.orderNumber != null &&
            i.duration != null,
        )
        // Exclude agenda items that are marked as "display without use cases" in Directus.
        // Treat missing/undefined as false (i.e., keep it).
        .filter((i) => {
          const directus = agendaByDirectusId.get(i.demonstrationAgendaDirectusId!);
          return directus?.display_without_usecases !== true;
        })
        .map((i) => {
          const directus = agendaByDirectusId.get(i.demonstrationAgendaDirectusId!);
          return {
            id: i.id!,
            demonstrationAgendaDirectusId: i.demonstrationAgendaDirectusId!,
            orderNumber: i.orderNumber!,
            duration: i.duration!,
            translations: directus?.translations ?? [],
          };
        });

      // Filter subcriteria by the IDs returned from backend and attach weights.
      const backendSubcriteria = evaluationDataResponse.subcriteria ?? [];
      const subcriteriaWeightById = new Map(
        backendSubcriteria
          .filter((sc) => sc.directusId)
          .map((sc) => [sc.directusId as string, sc.weight ?? null]),
      );
      const subcriteriaIds = new Set(
        backendSubcriteria.filter((sc) => sc.directusId).map((sc) => sc.directusId as string),
      );

      const filteredSubcriteria = allSubcriteria
        .filter((sc: EvaluationSubCriteriaDirectus) => subcriteriaIds.has(sc.id))
        .map((sc: EvaluationSubCriteriaDirectus) => ({
          ...sc,
          weight: subcriteriaWeightById.get(sc.id) ?? null,
        }));

      this.evaluationData.set({
        ...evaluationDataResponse,
        agendaItems: agendaItemsVm,
        subcriteria: filteredSubcriteria,
      });
    } catch (e) {
      console.error('Failed to load evaluation data', e);
    }
  }

  protected readonly hasParticipatingTools = computed(() => {
    return (this.evaluationData()?.tools?.length ?? 0) > 0;
  });

  protected readonly isCurrentUserSelected = computed(() => {
    const selectedEmail = this.selectedContributorEmail();
    const currentUserEmail = this.projectUserDetail()?.email;
    if (!selectedEmail) return true;
    if (selectedEmail === AVERAGE_USER_EMAIL) return false;
    if (!currentUserEmail) return false;
    return selectedEmail === currentUserEmail;
  });

  protected readonly isAverageSelected = computed(
    () => this.selectedContributorEmail() === AVERAGE_USER_EMAIL,
  );

  /**
   * User ids considered when showing "average results".
   *
   * Important: in this step both contributors and project managers can provide ratings,
   * so the average should include both roles and ignore only missing / "No Rating" values.
   */
  protected readonly averageContributorUserIds = computed(() => {
    return this.contributors()
      .filter(
        (u) =>
          u.roles.includes(ProjectRoleType.CONTRIBUTOR) ||
          u.roles.includes(ProjectRoleType.PROJECT_MANAGER),
      )
      .map((u) => u.userId);
  });

  onContributorSelected(email: string) {
    this.selectedContributorEmail.set(email);
  }

  async onSaveEvaluations(req: EvaluationUpsertRequest) {
    if (this.isCurrentStepLocked()) return;
    try {
      await firstValueFrom(
        this.evaluationService.upsertEvaluations({
          projectId: this.projectId,
          evaluationUpsertRequest: req,
        }),
      );
      await this.loadData();
      await this.stepState.loadStepStatuses(this.projectId);
    } catch (e) {
      console.error('Failed to save', e);
    }
  }

  onCommentOpen(event: {agendaItemId: number; toolId: number}) {
    this.currentCommentAgendaItemId = event.agendaItemId;
    this.currentCommentToolId = event.toolId;

    const data = this.evaluationData();
    const contributors = this.contributors();

    if (data) {
      // Get all comments for this agenda item and tool
      const relevantComments = (data.comments ?? []).filter(
        (c) =>
          c.demonstrationAgendaItemId === event.agendaItemId &&
          c.participatingToolId === event.toolId,
      );

      // Enrich comments with user data
      this.currentComments = relevantComments
        .map((comment) => {
          const user = contributors.find((u) => u.userId === comment.userId);
          return {
            ...comment,
            user,
          };
        })
        .sort((a, b) => {
          // Sort by createdAt ascending (oldest first)
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return aTime - bTime;
        });
    } else {
      this.currentComments = [];
    }

    this.commentModalVisible = true;
  }

  async onSaveComment(comment: string) {
    if (!this.currentCommentAgendaItemId || !this.currentCommentToolId) return;
    if (this.isCurrentStepLocked()) return;

    try {
      await firstValueFrom(
        this.evaluationService.createComment({
          projectId: this.projectId,
          evaluationCommentRequest: {
            demonstrationAgendaItemId: this.currentCommentAgendaItemId,
            participatingToolId: this.currentCommentToolId,
            comment,
          },
        }),
      );
      await this.loadData();

      // Refresh the comments in the modal
      const data = this.evaluationData();
      const contributors = this.contributors();

      if (data && this.currentCommentAgendaItemId && this.currentCommentToolId) {
        const relevantComments = (data.comments ?? []).filter(
          (c) =>
            c.demonstrationAgendaItemId === this.currentCommentAgendaItemId &&
            c.participatingToolId === this.currentCommentToolId,
        );

        this.currentComments = relevantComments
          .map((comment) => {
            const user = contributors.find((u) => u.userId === comment.userId);
            return {
              ...comment,
              user,
            };
          })
          .sort((a, b) => {
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return aTime - bTime;
          });
      }
    } catch (e) {
      console.error('Failed to save comment', e);
    }
  }

  completePhase() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to complete the RFD phase?',
      header: 'Complete Phase',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.evaluationService
          .completeProviderEvaluationStep({projectId: this.projectId})
          .subscribe({
            next: async () => {
              await this.stepState.loadStepStatuses(this.projectId);
            },
            error: (e) => {
              if (e?.status === 409) {
                if (
                  e?.error?.translationDetailKey ===
                  Step75ProviderEvaluationComponent.MINIMUM_PARTICIPANTS_ERROR_DETAIL_KEY
                ) {
                  this.confirmationService.confirm({
                    header: this.translocoService.translate(
                      'project.steps.rfd.evaluation.completePhase.minimumParticipantsHeader',
                    ),
                    message: this.translocoService.translate(
                      'project.steps.rfd.evaluation.completePhase.minimumParticipantsMessage',
                      {
                        minimum:
                          Step75ProviderEvaluationComponent.MIN_REQUIRED_TOOLS_TO_COMPLETE_PHASE,
                      },
                    ),
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: this.translocoService.translate('common.ok'),
                    rejectVisible: false,
                  });
                  return;
                }
                this.confirmationService.confirm({
                  header: 'Cannot complete phase',
                  message:
                    'Completing the phase is only possible once each provider and each matrix cell has at least one rating from an eligible evaluator.',
                  icon: 'pi pi-exclamation-triangle',
                  acceptLabel: 'OK',
                  rejectVisible: false,
                });
                return;
              }
              console.error('Failed to complete provider evaluation step', e);
            },
          });
      },
    });
  }

  protected readonly canCompletePhase = computed(() => {
    const detail = this.projectUserDetail();
    return detail?.roles?.includes(ProjectRoleType.PROJECT_MANAGER) ?? false;
  });

  protected readonly canInviteContributors = computed(() => {
    const detail = this.projectUserDetail();
    return detail?.roles?.includes(ProjectRoleType.PROJECT_MANAGER) ?? false;
  });

  protected openAddContributor() {
    this.router.navigate([APP_ROUTES.PROJECT, this.projectId, APP_ROUTES.STEPS], {
      queryParams: {
        openAddDialog: 'true',
        preselectedRole: ProjectRoleType.CONTRIBUTOR,
        preselectedPages: JSON.stringify([ProjectStepType.RFD_PROVIDER_EVALUATION]),
      },
    });
    this.stepState.setActiveSpecialOption(SPECIAL_OPTION_IDS.USER_MANAGEMENT);
  }

  protected readonly selectedContributorId = computed(() => {
    const email = this.selectedContributorEmail();
    if (!email) {
      return this.projectUserDetail()?.userId ?? null;
    }
    if (email === AVERAGE_USER_EMAIL) {
      // Average mode is handled explicitly by the matrix; keep user selection null.
      return null;
    }
    const found = this.contributors().find((u) => u.email === email);
    return found?.userId ?? null;
  });
}
